import React, { useState, useEffect, useRef } from 'react';
import { Users, User, Mail, Lock, Eye, EyeOff, ArrowLeft, Check, X, CheckCircle, XCircle } from 'lucide-react';
import { Button, Input, GardenBackground } from '../components/ui';
import { authService } from '../services/authService';

export const FamilyCreation = ({ onNavigate, onFamilyCreated, onboardingData = null }) => {
  const [step, setStep] = useState(1);
  const [familyData, setFamilyData] = useState({
    familyName: ''
  });
  const [guardianData, setGuardianData] = useState({
    firstName: '',
    lastName: '',
    email: onboardingData?.email || '',
    password: '',
    confirmPassword: '',
    role: 'parent'
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signUpCode, setSignUpCode] = useState(onboardingData?.invite_code || '');
  const [codeValidation, setCodeValidation] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const codeTimeoutRef = useRef(null);
  const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

  const isOnboarding = !!onboardingData;
  const [familyNameStatus, setFamilyNameStatus] = useState(null);
  const [familyNameMessage, setFamilyNameMessage] = useState('');

  const validateSignUpCode = async (code) => {
    if (!code.trim()) {
      setCodeValidation(null);
      return;
    }

    setValidatingCode(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/validate-signup-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });

      const result = await response.json();
      setCodeValidation(result.data || result);
    } catch (error) {
      setCodeValidation({ valid: false, message: 'Error validating code' });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSignUpCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setSignUpCode(code);
    setCodeValidation(null);

    if (codeTimeoutRef.current) {
      clearTimeout(codeTimeoutRef.current);
    }

    if (code.trim().length > 0) {
      setValidatingCode(true);
      codeTimeoutRef.current = setTimeout(() => {
        validateSignUpCode(code);
      }, 500);
    } else {
      setValidatingCode(false);
    }
  };

  useEffect(() => {
    const checkFamilyName = async () => {
      if (!familyData.familyName || familyData.familyName.trim().length < 2) {
        setFamilyNameStatus(null);
        setFamilyNameMessage('');
        return;
      }

      setFamilyNameStatus('checking');
      setFamilyNameMessage('Checking availability...');

      try {
        const result = await authService.checkFamilyName(familyData.familyName);
        if (result.available) {
          setFamilyNameStatus('available');
          setFamilyNameMessage('Available');
        } else {
          setFamilyNameStatus('taken');
          setFamilyNameMessage('Already taken');
        }
      } catch (error) {
        setFamilyNameStatus('error');
        setFamilyNameMessage('Error checking name');
      }
    };

    const timeoutId = setTimeout(checkFamilyName, 500);
    return () => clearTimeout(timeoutId);
  }, [familyData.familyName]);

  const validateStep1 = () => {
    const newErrors = {};

    if (!familyData.familyName.trim()) {
      newErrors.familyName = 'Family name is required';
    } else if (familyData.familyName.trim().length < 2) {
      newErrors.familyName = 'Family name must be at least 2 characters';
    } else if (familyNameStatus === 'taken') {
      newErrors.familyName = '';
    }

    if (signUpCode && signUpCode.trim() && !isOnboarding) {
      if (validatingCode) {
        newErrors.signUpCode = 'Please wait while we verify your code';
      } else if (codeValidation === null) {
        newErrors.signUpCode = 'Please enter a valid signup code or leave empty';
      } else if (codeValidation?.valid === false) {
        newErrors.signUpCode = codeValidation?.message || 'Invalid signup code';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!guardianData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!guardianData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!guardianData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(guardianData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!guardianData.password) {
      newErrors.password = 'Password is required';
    } else if (guardianData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (guardianData.password !== guardianData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Submit = () => {
    if (validateStep1()) {
      setStep(2);
      setErrors({});
    }
  };

  const handleStep2Submit = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const familyDataWithCode = {
        ...familyData,
        signUpCode: signUpCode.trim() || null
      };

      const result = await authService.createFamily(familyDataWithCode, guardianData);

      if (result.success) {
        onFamilyCreated(result.data);
      }
    } catch (error) {
      if (error.message.includes('email') && error.message.includes('exists')) {
        setErrors({ email: 'An account with this email already exists' });
      } else if (error.message.includes('email')) {
        setErrors({ email: error.message });
      } else if (error.message.includes('password')) {
        setErrors({ password: error.message });
      } else {
        setErrors({ general: error.message || 'An unexpected error occurred' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      onNavigate('welcome');
    } else {
      setStep(1);
      setErrors({});
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <GardenBackground />
      <div className="w-full max-w-sm relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

          {/* Step indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                step >= 1 ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                {step > 1 ? <Check className="w-3.5 h-3.5" /> : '1'}
              </div>
              <div className={`w-8 h-px ${step >= 2 ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                step >= 2 ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                2
              </div>
            </div>
          </div>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {step === 1 && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl text-gray-900 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Create Your Family</h2>
                <p className="text-sm text-gray-600">Let's set up your family's savings journey!</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Input
                    label="Family Name"
                    icon={Users}
                    value={familyData.familyName}
                    onChange={(e) => setFamilyData({ ...familyData, familyName: e.target.value })}
                    error={errors.familyName}
                    placeholder="The Johnson Family"
                    disabled={isSubmitting}
                  />

                  {familyData.familyName.trim().length >= 2 && familyNameMessage && (
                    <div className={`flex items-center gap-1.5 text-xs ${
                      familyNameStatus === 'available' ? 'text-emerald-600' :
                      familyNameStatus === 'taken' ? 'text-red-500' :
                      'text-gray-400'
                    }`}>
                      {familyNameStatus === 'checking' && (
                        <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                      )}
                      {familyNameStatus === 'available' && <Check className="w-3 h-3" />}
                      {familyNameStatus === 'taken' && <X className="w-3 h-3" />}
                      {familyNameMessage}
                    </div>
                  )}
                </div>

                {/* Sign-up Code */}
                <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Sign-up Code
                    <span className="text-gray-400 font-normal ml-1">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-500">
                    Have a code from your program? Enter it here.
                  </p>

                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g. SCHOOL2025"
                      value={signUpCode}
                      onChange={handleSignUpCodeChange}
                      disabled={isSubmitting || isOnboarding}
                      className="block w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-colors border-gray-200 font-mono text-center tracking-wider"
                    />

                    {signUpCode && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {validatingCode ? (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                        ) : codeValidation?.valid === true ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : codeValidation?.valid === false ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : null}
                      </div>
                    )}
                  </div>

                  {isOnboarding && (
                    <div className="flex items-center text-emerald-600 text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      Code provided by your program: {signUpCode}
                    </div>
                  )}

                  {codeValidation?.valid === true && (
                    <p className="text-xs text-emerald-600">
                      Valid code for: {codeValidation.program}
                    </p>
                  )}

                  {signUpCode && !validatingCode && codeValidation?.valid === false && (
                    <p className="text-xs text-red-500">Code does not exist</p>
                  )}

                  {!signUpCode && (
                    <p className="text-xs text-gray-400">
                      No code? No problem — you can create your family without one.
                    </p>
                  )}
                </div>

                <div className="pt-2 space-y-3">
                  <Button
                    onClick={handleStep1Submit}
                    disabled={
                      isSubmitting ||
                      !familyData.familyName.trim() ||
                      familyNameStatus === 'taken' ||
                      familyNameStatus === 'checking' ||
                      (!isOnboarding && validatingCode) ||
                      (!isOnboarding && signUpCode && signUpCode.trim() && codeValidation?.valid !== true)
                    }
                    size="lg"
                    className="w-full"
                  >
                    {validatingCode ? 'Validating Code...' : 'Continue'}
                  </Button>
                  <button
                    type="button"
                    className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center justify-center gap-1"
                    onClick={handleBack}
                    disabled={isSubmitting}
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl text-gray-900 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Almost There!</h2>
                <p className="text-sm text-gray-600">Set up your parent or guardian account</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="First Name"
                    icon={User}
                    value={guardianData.firstName}
                    onChange={(e) => setGuardianData({ ...guardianData, firstName: e.target.value })}
                    error={errors.firstName}
                    placeholder="Sarah"
                    disabled={isSubmitting}
                  />
                  <Input
                    label="Last Name"
                    icon={User}
                    value={guardianData.lastName}
                    onChange={(e) => setGuardianData({ ...guardianData, lastName: e.target.value })}
                    error={errors.lastName}
                    placeholder="Johnson"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Role */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Role in Family
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'parent', label: 'Parent' },
                      { value: 'guardian', label: 'Guardian' }
                    ].map((role) => (
                      <button
                        key={role.value}
                        onClick={() => setGuardianData({ ...guardianData, role: role.value })}
                        className={`
                          py-2 px-3 rounded-xl border text-sm font-medium transition-colors
                          ${guardianData.role === role.value
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }
                          ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        disabled={isSubmitting}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Input
                  label="Email Address"
                  type="email"
                  icon={Mail}
                  value={guardianData.email}
                  onChange={(e) => setGuardianData({ ...guardianData, email: e.target.value })}
                  error={errors.email}
                  placeholder="sarah@example.com"
                  disabled={isSubmitting || isOnboarding}
                />

                {isOnboarding && (
                  <p className="text-xs text-emerald-600 -mt-2">
                    Email from your program registration
                  </p>
                )}

                <div className="relative">
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    icon={Lock}
                    value={guardianData.password}
                    onChange={(e) => setGuardianData({ ...guardianData, password: e.target.value })}
                    error={errors.password}
                    placeholder="At least 6 characters"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className={`absolute right-3 top-[38px] text-gray-400 hover:text-gray-600 ${
                      isSubmitting ? 'pointer-events-none' : ''
                    }`}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSubmitting}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <div className="relative">
                  <Input
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    icon={Lock}
                    value={guardianData.confirmPassword}
                    onChange={(e) => setGuardianData({ ...guardianData, confirmPassword: e.target.value })}
                    error={errors.confirmPassword}
                    placeholder="Re-enter your password"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className={`absolute right-3 top-[38px] text-gray-400 hover:text-gray-600 ${
                      isSubmitting ? 'pointer-events-none' : ''
                    }`}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isSubmitting}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <div className="pt-2 space-y-3">
                  <Button
                    onClick={handleStep2Submit}
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating Family...' : 'Create Family & Account'}
                  </Button>
                  <button
                    type="button"
                    className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center justify-center gap-1"
                    onClick={handleBack}
                    disabled={isSubmitting}
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};